import { smallInfoLabel, Row } from 'components/GridItemList';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import { useSectionWideClass } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useDetailsCardFields } from 'pages/invoices/layout/views/general-tab/DetailsCard';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { collectCorrections } from 'pages/invoices/util/invoiceUtil';
import { Path, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { InvoiceForm } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { InvoiceTabProps } from '../invoiceTabProps';
import { InvoiceFormSection, NO_SAVE, saveFieldGroups, VALUE_ACTION_CLASS, yesNo } from './GeneralCard';

type Props = Pick<
    InvoiceTabProps,
    | 'invoice'
    | 'classifiers'
    | 'isInViewMode'
    | 'submitInvoice'
    | 'submitTemplateUpdate'
    | 'isTemplateEditable'
    | 'updateInvoiceLimited'
    | 'existingPrepayments'
>;

const TEMPLATE_FIELDS: Path<InvoiceForm>[] = ['invoiceTemplate', 'invoiceSpecTemplate'];
// The fields an invoice that no longer edits still takes from a user who may update every invoice.
const LIMITED_FIELDS: Path<InvoiceForm>[] = ['billingInstructions', 'excludeFromReporting'];
const INVOICE_FIELDS: Path<InvoiceForm>[] = [
    'invoicedByOffice',
    'signedByEmployee',
    'invoiceAccountingType',
    'vatCalculationType',
    'isSpecIncludedInInvoice',
    'isSpecExcludedFromEmail',
    'paymentNotificationReceivers',
];
const SETTINGS_FIELDS = [...TEMPLATE_FIELDS, ...LIMITED_FIELDS, ...INVOICE_FIELDS];

const invoiceLink = (invoice: { id: number; invoiceNumber?: string | null } | null | undefined) =>
    invoice ? <RecordLink to={`/invoice/${invoice.id}`} label={invoice.invoiceNumber ?? ''} /> : '-';

/**
 * Settings: classic's Details card without its payer and its cases, which have cards of their own. Its templates,
 * its two limited fields and the rest each save by the path they take today, on Save.
 */
export function SettingsSection(props: Props) {
    const { invoice, isInViewMode, isTemplateEditable, submitInvoice, submitTemplateUpdate, updateInvoiceLimited } =
        props;
    const fields = useDetailsCardFields({
        ...props,
        submitInvoice: NO_SAVE,
        submitTemplateUpdate: NO_SAVE,
        updateInvoiceLimited: NO_SAVE,
    });
    const methods = useFormContext<InvoiceForm>();
    const { control } = methods;
    const { dirtyFields } = useFormState({ control });
    const [
        office,
        invoiceTemplate,
        invoiceSpecTemplate,
        supervisedBy,
        signedBy,
        accountingType,
        vatCalculationType,
        isSpecIncluded,
        isSpecExcluded,
        billingInstructions,
        paymentNotificationReceivers,
        excludeFromReporting,
    ] = useWatch({
        control,
        name: [
            'invoicedByOffice',
            'invoiceTemplate',
            'invoiceSpecTemplate',
            'supervisedByEmployee',
            'signedByEmployee',
            'invoiceAccountingType',
            'vatCalculationType',
            'isSpecIncludedInInvoice',
            'isSpecExcludedFromEmail',
            'billingInstructions',
            'paymentNotificationReceivers',
            'excludeFromReporting',
        ],
    });
    const isAccountingTypeEnabled = useIsFeatureEnabled(FeatureFlag.EDIT_INVOICE_ACCOUNTING_TYPE);
    const isVatCalculationTypeEnabled = useIsFeatureEnabled(FeatureFlag.EDIT_INVOICE_VAT_CALCULATION_TYPE);
    const hasUpdatePermission = useHasPermissions(['UPDATE.INVOICES.ALL']);
    // A stored text keeps its whole row; without one the field is two columns of a few lines, beside the next.
    const instructionsWideClass = useSectionWideClass(control, 'billingInstructions');
    const { isCorrection, isCreditFromDebit, isPostponed } = useInvoice(invoice);
    const { isSharedPaymentCopy } = useSharedPayment(invoice);

    const customizeAction = fields.canCustomize ? (
        // Its words wrap too, on a cell narrower than the action.
        <Button
            variant="ghost"
            size="sm"
            className={cn(
                VALUE_ACTION_CLASS,
                'shrink text-start whitespace-normal @min-[17rem]:shrink-0 @min-[17rem]:whitespace-nowrap'
            )}
            onClick={fields.openCustomize}
        >
            <FormattedMessage id="invoices.custom.customize" />
        </Button>
    ) : undefined;

    const creditInvoice = invoice.hasCreditInvoice ? invoice.creditInvoices?.[0] : undefined;
    const copies = (invoice.invoiceCopies ?? []).filter((copy) => copy.id !== invoice.id);
    const linkRows: Row[] = [
        ...(isCorrection && invoice.originatesFromInvoice
            ? [
                  {
                      label: t('invoices.general-tab.details.originates-from'),
                      content: invoiceLink(invoice.originatesFromInvoice),
                  },
              ]
            : []),
        ...(invoice.correctionInvoice
            ? [
                  {
                      label: t('invoices.general-tab.details.correction-invoices'),
                      content: '-',
                      chips: collectCorrections(invoice.correctionInvoice).map((correction) => ({
                          key: correction.id,
                          label: invoiceLink(correction),
                          record: true,
                      })),
                  },
              ]
            : []),
        ...(invoice.isSplit
            ? [
                  {
                      label: t(
                          isSharedPaymentCopy
                              ? 'invoices.general-tab.details.shared-payment-with'
                              : 'invoices.general-tab.details.copy-of'
                      ),
                      content: '-',
                      chips: copies.map((copy) => ({ key: copy.id, label: invoiceLink(copy), record: true })),
                  },
              ]
            : []),
        ...(creditInvoice
            ? [{ label: t('invoices.general-tab.details.credit-invoice'), content: invoiceLink(creditInvoice) }]
            : []),
        ...(isCreditFromDebit
            ? [{ label: t('invoices.general-tab.details.debit-invoice'), content: invoiceLink(invoice.debitInvoice) }]
            : []),
    ];

    const rows: Row[] = [
        {
            label: t('invoices.general-tab.details.issued-by-office'),
            content: office ? <RecordLink to={`/office/${office.id}`} label={office.name} /> : '-',
        },
        {
            label: t('invoices.general-tab.details.invoice-template'),
            // In the value, beside the name, which wraps to make room; in a cell too narrow for both, under it.
            content: customizeAction ? (
                <span className="@container block">
                    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 @min-[17rem]:flex-nowrap">
                        <span className="min-w-0 break-words">{invoiceTemplate?.name || '-'}</span>
                        {customizeAction}
                    </span>
                </span>
            ) : (
                invoiceTemplate?.name || '-'
            ),
        },
        {
            label: t('invoices.general-tab.details.invoice-spec-template'),
            content: invoiceSpecTemplate?.name || '-',
        },
        {
            label: t('invoices.general-tab.details.supervised-by'),
            content: supervisedBy?.id ? (
                <EmployeeNameLink to={`/employee/${supervisedBy.id}`} entity={supervisedBy} />
            ) : (
                '-'
            ),
        },
        {
            label: smallInfoLabel(
                'invoices.general-tab.details.signed-by',
                'invoices.general-tab.details.signed-by-info'
            ),
            content: signedBy?.id ? <EmployeeNameLink to={`/employee/${signedBy.id}`} entity={signedBy} /> : '-',
        },
        {
            label: t('invoices.general-tab.details.invoice-accounting-type'),
            content: accountingType?.label || '-',
            hidden: !isAccountingTypeEnabled,
        },
        {
            label: t('invoices.general-tab.details.vat-calculation-type'),
            content: vatCalculationType?.label || '-',
            hidden: !isVatCalculationTypeEnabled,
        },
        { label: t('invoices.general-tab.details.merge-invoice-and-spec'), content: yesNo(isSpecIncluded) },
        {
            label: t('invoices.general-tab.details.exclude-spec-pdf-attachment-in-email'),
            content: yesNo(isSpecExcluded),
        },
        {
            label: t('invoices.general-tab.details.specific-instructions'),
            content: billingInstructions || '-',
            multiline: true,
        },
        {
            label: t('contacts.billing.invoice-payment-notification-to'),
            content: '-',
            chips: (paymentNotificationReceivers ?? []).map((employee) => ({
                key: employee.id,
                label: <EmployeeNameLink to={`/employee/${employee.id}`} entity={employee} />,
                record: true,
            })),
        },
        {
            label: t('invoices.general-tab.details.payer-e-billing-system-url'),
            content: fields.clientParty?.externalBillingInfo ? (
                <a
                    href={fields.clientParty.externalBillingInfo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link w-fit no-underline hover:underline"
                >
                    {fields.clientParty.externalBillingInfo}
                </a>
            ) : (
                '-'
            ),
            hidden: !fields.clientParty,
        },
        {
            label: t('invoices.general-tab.invoice-details.exclude-from-report'),
            content: yesNo(excludeFromReporting),
            hidden: !fields.showsExcludeFromQlikSense,
        },
        ...linkRows,
    ];

    // The template first: its own save clears the customization a new template leaves stale, which the full save,
    // carrying the template too, does not.
    const save = () =>
        saveFieldGroups(methods, dirtyFields, [
            [TEMPLATE_FIELDS, submitTemplateUpdate],
            [LIMITED_FIELDS, updateInvoiceLimited],
            [INVOICE_FIELDS, submitInvoice],
        ]);

    return (
        <InvoiceFormSection
            id="settings"
            titleId="invoices.general-tab.details.title"
            invoice={invoice}
            canEdit={!isInViewMode || isTemplateEditable || (hasUpdatePermission && !isPostponed)}
            rows={rows}
            fieldNames={SETTINGS_FIELDS}
            save={save}
            fields={
                <>
                    {fields.officeField}
                    {fields.invoiceTemplateField(customizeAction)}
                    {fields.specTemplateField}
                    {fields.supervisedByField}
                    {fields.signedByField}
                    {fields.accountingTypeField}
                    {fields.vatCalculationField}
                    {fields.includeSpecField}
                    <div className="form-field flex flex-col">
                        {fields.excludeSpecField}
                        {fields.isSpecIncludedInInvoice && !isInViewMode ? (
                            <p className="text-muted-foreground text-caption m-0 mt-1">
                                <FormattedMessage id="invoices.general-tab.details.exclude-spec-disabled-reason" />
                            </p>
                        ) : null}
                    </div>
                    <div
                        className={cn(
                            'form-field min-w-0 [&_textarea]:min-h-16',
                            instructionsWideClass ?? '@2xs:col-span-2'
                        )}
                    >
                        {fields.specificInstructionsField}
                    </div>
                    {fields.paymentNotificationField}
                    {fields.eBillingUrlField}
                    {fields.excludeFromQlikSenseField}
                    {fields.linkFields}
                </>
            }
        />
    );
}
